import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Save, Loader2, Calendar, Info, Zap, Eye, Clock, PauseCircle,
  FileText, Tag,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useConfigByGrupo, useUpdateConfig } from "@/features/config/api";
import { apiCall, type ApiError } from "@/lib/api";
import { ReglasMotorCard } from "./ReglasMotorCard";

// Keys de facturación
const KEY_DIAS = "facturacion.dias_anticipacion";
const KEY_FACT_HORA = "facturacion.hora_ejecucion";
// Keys nuevas v1.1.0-rc7 — facturación 100% configurable
const KEY_CONCEPTO_TPL = "facturacion.concepto_template";
const KEY_CFDI_PROD = "facturacion.cfdi_clave_prod_serv";
const KEY_CFDI_UNIDAD = "facturacion.cfdi_clave_unidad";
// Keys de cobranza
const KEY_COB_DIAS_GRACIA = "cobranza.dias_gracia";
const KEY_COB_HORA = "cobranza.hora_corte";
// rc127 — promesas de pago (las claves existían desde rc106 pero no se exponían;
// el tope anual quedaba atorado en el 3 sembrado por la migración 018)
const KEY_PROM_MAX_DIAS = "cobranza.promesa_max_dias";
const KEY_PROM_MAX_ANIO = "cobranza.promesa_max_por_anio";
const KEY_PROM_GRACIA = "cobranza.promesa_dias_gracia_post_vencimiento";

type CicloResult = {
  hoy: string;
  dias_anticipacion: number;
  candidatos: number;
  emitidas: number;
  errores: number;
  folios_emitidos: string[];
  errores_detalle: { codigo: string; error: string }[];
  dry_run: boolean;
  preview?: {
    codigo: string;
    vencimiento: string;
    periodo_inicio: string;
    periodo_fin: string;
    dias_restantes_al_corte: number;
  }[];
};

export function ConfigFacturacionSection() {
  const { data: items, isLoading: loadingFact } = useConfigByGrupo("facturacion");
  const { data: itemsCob, isLoading: loadingCob } = useConfigByGrupo("cobranza");
  const updateMut = useUpdateConfig();
  const isLoading = loadingFact || loadingCob;

  // Facturación
  const [diasAnticipacion, setDiasAnticipacion] = useState<string>("5");
  const [factHora, setFactHora] = useState<string>("09:00");
  // v1.1.0-rc7: keys nuevas (template + códigos SAT + interval del poller)
  const [conceptoTpl, setConceptoTpl] = useState<string>("Servicio de Internet {plan} — {contrato_codigo}");
  const [cfdiProd, setCfdiProd] = useState<string>("81161700");
  const [cfdiUnidad, setCfdiUnidad] = useState<string>("E48");
  // Cobranza
  const [cobDiasGracia, setCobDiasGracia] = useState<string>("5");
  const [cobHora, setCobHora] = useState<string>("08:00");
  // rc127 — promesas
  const [promMaxDias, setPromMaxDias] = useState<string>("15");
  const [promMaxAnio, setPromMaxAnio] = useState<string>("0");
  const [promGracia, setPromGracia] = useState<string>("1");

  const [dirty, setDirty] = useState(false);
  // Ejecución manual (F20.9)
  const [running, setRunning] = useState<"dry" | "real" | null>(null);
  const [lastResult, setLastResult] = useState<CicloResult | null>(null);

  useEffect(() => {
    if (items) {
      const dias = items.find((i) => i.key === KEY_DIAS);
      const hora = items.find((i) => i.key === KEY_FACT_HORA);
      const tpl = items.find((i) => i.key === KEY_CONCEPTO_TPL);
      const cprod = items.find((i) => i.key === KEY_CFDI_PROD);
      const cunidad = items.find((i) => i.key === KEY_CFDI_UNIDAD);
      setDiasAnticipacion(String(dias?.value ?? 5));
      setFactHora(String(hora?.value ?? "09:00"));
      setConceptoTpl(String(tpl?.value ?? "Servicio de Internet {plan} — {contrato_codigo}"));
      setCfdiProd(String(cprod?.value ?? "81161700"));
      setCfdiUnidad(String(cunidad?.value ?? "E48"));
    }
  }, [items]);

  useEffect(() => {
    if (itemsCob) {
      const dias = itemsCob.find((i) => i.key === KEY_COB_DIAS_GRACIA);
      const hora = itemsCob.find((i) => i.key === KEY_COB_HORA);
      setCobDiasGracia(String(dias?.value ?? 5));
      setCobHora(String(hora?.value ?? "08:00"));
      setPromMaxDias(String(itemsCob.find((i) => i.key === KEY_PROM_MAX_DIAS)?.value ?? 15));
      setPromMaxAnio(String(itemsCob.find((i) => i.key === KEY_PROM_MAX_ANIO)?.value ?? 0));
      setPromGracia(String(itemsCob.find((i) => i.key === KEY_PROM_GRACIA)?.value ?? 1));
    }
  }, [itemsCob]);

  // Reset dirty cuando carga nueva data
  useEffect(() => {
    if (items && itemsCob) setDirty(false);
  }, [items, itemsCob]);

  async function handleEjecutarCiclo(dryRun: boolean) {
    setRunning(dryRun ? "dry" : "real");
    try {
      const res = await apiCall<CicloResult>(
        "post", "/api/v1/auto-facturacion/ejecutar",
        { json: { dry_run: dryRun } },
      );
      setLastResult(res);
      if (dryRun) {
        toast.info(`Preview: ${res.candidatos} contratos se facturarían`, {
          description: res.candidatos === 0
            ? "Nadie llega al umbral de corte hoy."
            : `Usa el botón "Ejecutar real" para emitir.`,
        });
      } else {
        if (res.emitidas > 0) {
          toast.success(`${res.emitidas} factura(s) emitida(s)`, {
            description: res.errores > 0
              ? `${res.errores} error(es) — revisa el detalle abajo`
              : "Todas sin errores",
          });
        } else if (res.errores > 0) {
          toast.error(`${res.errores} error(es) durante el ciclo`, {
            description: "Revisa el detalle abajo",
          });
        } else {
          toast.info("Ciclo completado — nadie llega al umbral hoy");
        }
      }
    } catch (e) {
      toast.error("Error ejecutando ciclo", {
        description: (e as ApiError).message,
      });
    } finally {
      setRunning(null);
    }
  }

  function validarHora(s: string): boolean {
    // HH:MM con HH 0-23 y MM 0-59
    return /^(?:[01]\d|2[0-3]):[0-5]\d$/.test(s);
  }

  async function handleSave() {
    // Validaciones
    const diasNum = Number(diasAnticipacion);
    if (!Number.isInteger(diasNum) || diasNum < 1 || diasNum > 30) {
      toast.error("Días de anticipación inválidos", {
        description: "Debe ser un entero entre 1 y 30.",
      });
      return;
    }
    if (!validarHora(factHora)) {
      toast.error("Hora de emisión inválida",
        { description: "Formato HH:MM (24h). Ej. 09:00, 14:30." });
      return;
    }
    const cobDiasNum = Number(cobDiasGracia);
    if (!Number.isInteger(cobDiasNum) || cobDiasNum < 0 || cobDiasNum > 30) {
      toast.error("Días de gracia inválidos", {
        description: "Debe ser un entero entre 0 y 30.",
      });
      return;
    }
    const promDiasNum = Number(promMaxDias);
    if (!Number.isInteger(promDiasNum) || promDiasNum < 1 || promDiasNum > 90) {
      toast.error("Promesas: el máximo de días debe ser entero entre 1 y 90");
      return;
    }
    const promAnioNum = Number(promMaxAnio);
    if (!Number.isInteger(promAnioNum) || promAnioNum < 0 || promAnioNum > 100) {
      toast.error("Promesas: el tope anual debe ser entero entre 0 (sin límite) y 100");
      return;
    }
    const promGraciaNum = Number(promGracia);
    if (!Number.isInteger(promGraciaNum) || promGraciaNum < 0 || promGraciaNum > 30) {
      toast.error("Promesas: los días de gracia deben ser entero entre 0 y 30");
      return;
    }
    if (!validarHora(cobHora)) {
      toast.error("Hora de corte inválida",
        { description: "Formato HH:MM (24h). Ej. 08:00, 20:30." });
      return;
    }
    // v1.1.0-rc7: validaciones de los nuevos campos
    if (!conceptoTpl.trim() || conceptoTpl.length > 200) {
      toast.error("Template del concepto inválido", {
        description: "Debe tener entre 1 y 200 caracteres.",
      });
      return;
    }
    if (!/^\d{6,8}$/.test(cfdiProd)) {
      toast.error("Código SAT prod_serv inválido", {
        description: "Debe ser numérico de 6 a 8 dígitos. Default 81161700.",
      });
      return;
    }
    if (!/^[A-Z0-9]{2,4}$/.test(cfdiUnidad)) {
      toast.error("Código SAT clave_unidad inválido", {
        description: "Debe ser alfanumérico de 2 a 4 caracteres. Default E48.",
      });
      return;
    }

    try {
      // Guardar todo en paralelo — facturacion y cobranza (rc129: sin claves muertas)
      await Promise.all([
        // Facturación
        updateMut.mutateAsync({
          key: KEY_DIAS, value: diasNum, value_type: "int", grupo: "facturacion",
          descripcion: "Días antes del corte en que el poller emite la factura del mes.",
        }),
        updateMut.mutateAsync({
          key: KEY_FACT_HORA, value: factHora, value_type: "string", grupo: "facturacion",
          descripcion: "Hora local (HH:MM) en que el poller de facturación dispara el ciclo diario.",
        }),
        // v1.1.0-rc7: nuevas keys de facturación
        updateMut.mutateAsync({
          key: KEY_CONCEPTO_TPL, value: conceptoTpl, value_type: "string", grupo: "facturacion",
          descripcion: "Template del concepto. Variables: {plan}, {contrato_codigo}, {cliente_codigo}, {periodo}.",
        }),
        updateMut.mutateAsync({
          key: KEY_CFDI_PROD, value: cfdiProd, value_type: "string", grupo: "facturacion",
          descripcion: "Código SAT de producto/servicio. 81161700 = Telecomunicaciones.",
        }),
        updateMut.mutateAsync({
          key: KEY_CFDI_UNIDAD, value: cfdiUnidad, value_type: "string", grupo: "facturacion",
          descripcion: "Código SAT de unidad. E48 = Unidad de servicio.",
        }),
        // Cobranza
        updateMut.mutateAsync({
          key: KEY_COB_DIAS_GRACIA, value: cobDiasNum, value_type: "int", grupo: "cobranza",
          descripcion: "Días de gracia después del vencimiento antes de que una factura se considere vencida y dispare suspensión.",
        }),
        updateMut.mutateAsync({
          key: KEY_COB_HORA, value: cobHora, value_type: "string", grupo: "cobranza",
          descripcion: "Hora local (HH:MM) en que el poller de cobranza dispara el ciclo diario de suspensiones.",
        }),
        // rc127 — promesas de pago
        updateMut.mutateAsync({
          key: KEY_PROM_MAX_DIAS, value: promDiasNum, value_type: "int", grupo: "cobranza",
          descripcion: "Máximo de días hacia adelante que se puede prometer.",
        }),
        updateMut.mutateAsync({
          key: KEY_PROM_MAX_ANIO, value: promAnioNum, value_type: "int", grupo: "cobranza",
          descripcion: "Máximo de promesas por cliente en 12 meses móviles. 0 = sin límite.",
        }),
        updateMut.mutateAsync({
          key: KEY_PROM_GRACIA, value: promGraciaNum, value_type: "int", grupo: "cobranza",
          descripcion: "Días después de la fecha prometida antes de marcar la promesa como vencida.",
        }),
      ]);
      toast.success("Configuración guardada", {
        description: "Los pollers tomarán los nuevos valores en el próximo ciclo.",
      });
      setDirty(false);
    } catch (e) {
      toast.error("Error al guardar", {
        description: (e as ApiError).message,
      });
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8 text-muted-foreground">
        <Loader2 size={20} className="animate-spin" />
      </div>
    );
  }

  return (
    // v1.5.0-rc5: id para que /ajustes#auto-facturacion funcione como deep-link
    <div id="auto-facturacion" className="grid grid-cols-1 lg:grid-cols-2 gap-4 scroll-mt-20">
      <div className="lg:col-span-2 flex items-start gap-2 rounded-md border border-primary/30 bg-primary/5 p-3 text-xs text-muted-foreground">
        <Info size={14} className="mt-0.5 shrink-0 text-primary" />
        <div>
          Estos ajustes controlan cómo y cuándo se generan facturas automáticamente.
          El <b>poller de facturación</b> (F20.9, próximo) corre diario y emite las
          facturas del mes cuando faltan pocos días al corte de cada contrato.
        </div>
      </div>

      {/* Días de anticipación */}
      <div className="rounded-lg border bg-card p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Calendar size={14} className="text-primary" />
          <Label className="text-sm font-semibold">
            Días de anticipación para emisión
          </Label>
        </div>
        <div className="flex items-center gap-3">
          <Input
            type="number"
            min={1}
            max={30}
            value={diasAnticipacion}
            onChange={(e) => { setDiasAnticipacion(e.target.value); setDirty(true); }}
            className="w-24 font-mono"
          />
          <span className="text-xs text-muted-foreground">días antes del corte</span>
        </div>
        <p className="text-[11px] text-muted-foreground">
          Ejemplo: si un contrato tiene <b>día de corte = 10</b> y este valor está
          en <b>5</b>, la factura de ese ciclo se emitirá el día 5 del mes, dando
          al cliente 5 días para pagar antes del corte. Rango válido: 1–30 días.
        </p>
      </div>

      {/* F20.9.1: Horario del poller de facturación */}
      <div className="rounded-lg border bg-card p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Clock size={14} className="text-primary" />
          <Label className="text-sm font-semibold">
            Horario del poller de facturación
          </Label>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground">
              Hora de emisión (24h)
            </label>
            <Input
              type="time"
              value={factHora}
              onChange={(e) => { setFactHora(e.target.value); setDirty(true); }}
              className="w-32 font-mono"
            />
          </div>
        </div>
        <p className="text-[11px] text-muted-foreground">
          El poller de facturación duerme hasta las <b>{factHora}</b> (hora local
          del servidor) y emite las facturas del ciclo una sola vez al día. Si
          cambias la hora, aplica en menos de un minuto.
        </p>
      </div>

      {/* v1.1.0-rc7: Template del concepto */}
      <div className="rounded-lg border bg-card p-4 space-y-3">
        <div className="flex items-center gap-2">
          <FileText size={14} className="text-primary" />
          <Label className="text-sm font-semibold">
            Concepto de la factura
          </Label>
        </div>
        <Input
          value={conceptoTpl}
          onChange={(e) => { setConceptoTpl(e.target.value); setDirty(true); }}
          maxLength={200}
          placeholder="Servicio de Internet {plan} — {contrato_codigo}"
          className="font-mono text-xs"
        />
        <div className="text-[11px] text-muted-foreground space-y-1">
          <p>
            Texto que aparece en cada línea de factura. Soporta variables que se
            sustituyen al emitir:
          </p>
          <ul className="list-disc pl-4 space-y-0.5">
            <li><code>{"{plan}"}</code> — nombre del plan del contrato</li>
            <li><code>{"{contrato_codigo}"}</code> — código del contrato (ej. C-000123)</li>
            <li><code>{"{cliente_codigo}"}</code> — código del cliente (ej. EMP-000001)</li>
            <li><code>{"{periodo}"}</code> — mes y año en español (ej. <code>may 2026</code>)</li>
          </ul>
          <p className="pt-1">
            Vista previa con tu plan ejemplo:{" "}
            <code className="bg-muted px-1 rounded">
              {conceptoTpl
                .replaceAll("{plan}", "Internet 50/10")
                .replaceAll("{contrato_codigo}", "C-000123")
                .replaceAll("{cliente_codigo}", "EMP-000001")
                .replaceAll("{periodo}", "may 2026")}
            </code>
          </p>
        </div>
      </div>

      {/* v1.1.0-rc7: Códigos SAT */}
      <div className="rounded-lg border bg-card p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Tag size={14} className="text-primary" />
          <Label className="text-sm font-semibold">
            Códigos SAT (catálogo CFDI)
          </Label>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground">
              Clave producto/servicio
            </label>
            <Input
              value={cfdiProd}
              onChange={(e) => { setCfdiProd(e.target.value); setDirty(true); }}
              maxLength={8}
              className="w-32 font-mono"
            />
            <span className="text-[10px] text-muted-foreground">
              Default <b>81161700</b> (Telecomunicaciones)
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground">
              Clave unidad
            </label>
            <Input
              value={cfdiUnidad}
              onChange={(e) => { setCfdiUnidad(e.target.value.toUpperCase()); setDirty(true); }}
              maxLength={4}
              className="w-24 font-mono"
            />
            <span className="text-[10px] text-muted-foreground">
              Default <b>E48</b> (Unidad de servicio)
            </span>
          </div>
        </div>
        <p className="text-[11px] text-muted-foreground">
          Estos códigos los exige el SAT al timbrar el CFDI. Solo cambiar si el
          SAT actualiza sus catálogos (suceso muy raro). Los defaults son los
          correctos para un ISP en México.
        </p>
      </div>

      {/* F20.9.1: Configuración de cobranza automática */}
      <div className="lg:col-span-2 rounded-lg border bg-card p-4 space-y-3">
        <div className="flex items-center gap-2">
          <PauseCircle size={14} className="text-destructive" />
          <Label className="text-sm font-semibold">
            Cobranza automática (suspensión por falta de pago)
          </Label>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground">
              Días de gracia
            </label>
            <Input
              type="number" min={0} max={30}
              value={cobDiasGracia}
              onChange={(e) => { setCobDiasGracia(e.target.value); setDirty(true); }}
              className="w-24 font-mono"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground">
              Hora de corte (24h)
            </label>
            <Input
              type="time"
              value={cobHora}
              onChange={(e) => { setCobHora(e.target.value); setDirty(true); }}
              className="w-32 font-mono"
            />
          </div>
        </div>
        <p className="text-[11px] text-muted-foreground">
          El poller de <code>byteisp-poller-cobranza</code> corre a esa hora, una vez al día.
          Una mensualidad se considera operativamente vencida cuando pasan{" "}
          <b>{cobDiasGracia}</b> días del vencimiento sin pagarse: la suspensión
          ocurre al día siguiente de agotarse la gracia (gracia {cobDiasGracia} →
          corte el día {Number(cobDiasGracia) + 1} después del vencimiento), según el
          alcance elegido abajo en "Reglas del motor" (por contrato o por cliente).
          Los contratos con <b>días de gracia override</b> (a nivel contrato) ignoran
          este valor global. La ventana típica es temprano AM para que soporte pueda
          atender llamadas del cliente suspendido durante el día.
        </p>
      </div>

      {/* rc127 — Promesas de pago */}
      <div className="lg:col-span-2 rounded-lg border bg-card p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Calendar size={14} className="text-primary" />
          <Label className="text-sm font-semibold">Promesas de pago</Label>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground">
              Máx. días a prometer
            </label>
            <Input
              type="number" min={1} max={90}
              value={promMaxDias}
              onChange={(e) => { setPromMaxDias(e.target.value); setDirty(true); }}
              className="w-24 font-mono"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground">
              Tope por cliente / 12 meses
            </label>
            <Input
              type="number" min={0} max={100}
              value={promMaxAnio}
              onChange={(e) => { setPromMaxAnio(e.target.value); setDirty(true); }}
              className="w-24 font-mono"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground">
              Gracia tras la fecha (días)
            </label>
            <Input
              type="number" min={0} max={30}
              value={promGracia}
              onChange={(e) => { setPromGracia(e.target.value); setDirty(true); }}
              className="w-24 font-mono"
            />
          </div>
        </div>
        <p className="text-[11px] text-muted-foreground">
          Una promesa puede fijarse hasta <b>{promMaxDias}</b> días adelante. El tope anual
          cuenta todas las promesas del cliente en 12 meses móviles (menos las canceladas):{" "}
          <b>{Number(promMaxAnio) === 0 ? "sin límite" : `máximo ${promMaxAnio}`}</b>. Pasada la
          fecha prometida más <b>{promGracia}</b> día(s) de gracia, la promesa se marca vencida y
          el contrato vuelve al flujo normal de cobranza.
        </p>
      </div>

      {/* rc143 — reglas del motor + cómo decide (guardado propio) */}
      <ReglasMotorCard />

      <div className="lg:col-span-2 flex items-center justify-end gap-2">
        {dirty && (
          <span className="text-xs text-muted-foreground">Cambios sin guardar</span>
        )}
        <Button size="sm" onClick={handleSave} disabled={!dirty || updateMut.isPending}>
          <Save size={12} />
          {updateMut.isPending ? "Guardando…" : "Guardar configuración"}
        </Button>
      </div>

      {/* Ejecución manual del ciclo (F20.9) */}
      <div className="lg:col-span-2 mt-6 rounded-lg border bg-card p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Zap size={14} className="text-primary" />
          <Label className="text-sm font-semibold">
            Ejecución manual del ciclo
          </Label>
        </div>
        <p className="text-[11px] text-muted-foreground">
          El poller <code>byteisp-poller-facturacion</code> corre automáticamente en el
          VPS en la ventana configurada (default 09:00 ±10 min). Si quieres
          disparar el ciclo manualmente AHORA:
        </p>
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm" variant="outline"
            onClick={() => handleEjecutarCiclo(true)}
            disabled={running !== null}
          >
            <Eye size={12} />
            {running === "dry" ? "Calculando…" : "Ver preview (dry run)"}
          </Button>
          <Button
            size="sm"
            onClick={() => handleEjecutarCiclo(false)}
            disabled={running !== null}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            <Zap size={12} />
            {running === "real" ? "Ejecutando…" : "Ejecutar ciclo ahora"}
          </Button>
        </div>
        <p className="text-[10px] text-muted-foreground">
          El ciclo es <b>idempotente</b>: si la factura del ciclo ya existe para
          un contrato, se salta. Puedes correrlo 100 veces — nunca duplica.
        </p>

        {/* Resultado del último ciclo */}
        {lastResult && (
          <div className="mt-2 rounded-md border bg-muted/30 p-3 space-y-2 text-xs">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-semibold">
                {lastResult.dry_run ? "Preview" : "Resultado"} —{" "}
                {lastResult.hoy} · anticipación {lastResult.dias_anticipacion}d
              </span>
            </div>
            <div className="flex flex-wrap gap-3">
              <span>Candidatos: <b>{lastResult.candidatos}</b></span>
              {!lastResult.dry_run && (
                <>
                  <span className="text-emerald-700 dark:text-emerald-400">
                    Emitidas: <b>{lastResult.emitidas}</b>
                  </span>
                  <span className={lastResult.errores > 0 ? "text-destructive" : "text-muted-foreground"}>
                    Errores: <b>{lastResult.errores}</b>
                  </span>
                </>
              )}
            </div>
            {lastResult.dry_run && lastResult.preview && lastResult.preview.length > 0 && (
              <div className="rounded border bg-card p-2 space-y-0.5 max-h-56 overflow-y-auto">
                {lastResult.preview.slice(0, 20).map((p, i) => (
                  <div key={i} className="flex flex-wrap gap-2 text-[11px]">
                    <span className="font-mono">{p.codigo}</span>
                    <span className="text-muted-foreground">→ vence {p.vencimiento}</span>
                    <span className="text-muted-foreground">
                      ({p.dias_restantes_al_corte}d al corte)
                    </span>
                  </div>
                ))}
                {lastResult.preview.length > 20 && (
                  <p className="text-[10px] text-muted-foreground italic">
                    +{lastResult.preview.length - 20} más…
                  </p>
                )}
              </div>
            )}
            {!lastResult.dry_run && lastResult.folios_emitidos.length > 0 && (
              <div className="rounded border bg-card p-2 text-[11px] font-mono">
                <span className="text-muted-foreground">Folios emitidos: </span>
                {lastResult.folios_emitidos.join(", ")}
              </div>
            )}
            {lastResult.errores_detalle.length > 0 && (
              <div className="rounded border border-destructive/40 bg-destructive/5 p-2 space-y-1">
                <p className="text-[10px] font-semibold text-destructive">
                  Errores:
                </p>
                {lastResult.errores_detalle.slice(0, 5).map((e, i) => (
                  <div key={i} className="text-[11px]">
                    <span className="font-mono">{e.codigo}</span>:{" "}
                    <span className="text-destructive">{e.error}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
